import React from 'react';
import {
  Alert,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getSupportAssignments,
  type SupportAssignment,
} from '../services/apiService.ts';
import { useTenant } from '../components/ContextTenant.tsx';

type LocationGroup = {
  key: string;
  tenantId: number | null;
  tenantName: string | null;
  locationId: number | null;
  locationName: string | null;
  rows: SupportAssignment[];
};

function buildLocationGroups(rows: SupportAssignment[]): LocationGroup[] {
  const groups = new Map<string, LocationGroup>();

  rows.forEach((row) => {
    const key = `${row.tenantId ?? 'tenant'}:${row.locationId ?? 'location'}`;
    const group = groups.get(key);
    if (group) {
      group.rows.push(row);
      return;
    }

    groups.set(key, {
      key,
      tenantId: row.tenantId,
      tenantName: row.tenantName,
      locationId: row.locationId,
      locationName: row.locationName,
      rows: [row],
    });
  });

  return [...groups.values()].sort((left, right) => {
    const tenantCompare = String(left.tenantName || left.tenantId || '').localeCompare(
      String(right.tenantName || right.tenantId || ''),
      'ro',
    );
    if (tenantCompare !== 0) return tenantCompare;
    return String(left.locationName || left.locationId || '').localeCompare(
      String(right.locationName || right.locationId || ''),
      'ro',
    );
  });
}

const LocationTeamPage: React.FC = () => {
  const { tenantId: scopedTenantId } = useTenant();
  const latestRequestRef = React.useRef(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<SupportAssignment[]>([]);
  const [tenantFilter, setTenantFilter] = React.useState(
    scopedTenantId ? String(scopedTenantId) : '',
  );
  const [locationFilter, setLocationFilter] = React.useState('');

  React.useEffect(() => {
    setTenantFilter(scopedTenantId ? String(scopedTenantId) : '');
    setLocationFilter('');
  }, [scopedTenantId]);

  const load = React.useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    const activeTenantFilter = tenantFilter || (scopedTenantId ? String(scopedTenantId) : '');

    setLoading(true);
    setError(null);
    try {
      const nextRows = await getSupportAssignments({
        tenantId: activeTenantFilter || undefined,
        locationId: locationFilter || undefined,
      });
      if (latestRequestRef.current !== requestId) {
        return;
      }
      setRows(Array.isArray(nextRows) ? nextRows : []);
    } catch (err: any) {
      if (latestRequestRef.current !== requestId) {
        return;
      }
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Nu am putut incarca echipele pe locatie.',
      );
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [locationFilter, scopedTenantId, tenantFilter]);

  React.useEffect(() => {
    load();
  }, [load]);

  const groups = React.useMemo(() => buildLocationGroups(rows), [rows]);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Internal Access
        </Typography>
        <Typography variant="h4">Location Team</Typography>
        <Typography color="text.secondary">
          Vedere orientata pe locatie pentru a intelege rapid cine opereaza acolo,
          cu ce profil si cu ce override-uri active.
        </Typography>
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Tenant ID"
            value={tenantFilter}
            onChange={(event) => setTenantFilter(event.target.value)}
            size="small"
          />
          <TextField
            label="Location ID"
            value={locationFilter}
            onChange={(event) => setLocationFilter(event.target.value)}
            size="small"
          />
          <Button variant="contained" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Stack spacing={1} sx={{ minWidth: 240 }}>
            <Typography variant="subtitle1">Locations</Typography>
            <Typography variant="h5">{groups.length}</Typography>
          </Stack>
          <Stack spacing={1} sx={{ minWidth: 240 }}>
            <Typography variant="subtitle1">Assignments</Typography>
            <Typography variant="h5">{rows.length}</Typography>
          </Stack>
          <Stack spacing={1} sx={{ minWidth: 240 }}>
            <Typography variant="subtitle1">Primary assignments</Typography>
            <Typography variant="h5">
              {rows.filter((row) => row.primary).length}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {groups.map((group) => (
        <Paper key={group.key} sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
            >
              <Stack spacing={0.5}>
                <Typography variant="h6">
                  {group.locationName || `Location ${group.locationId ?? '-'}`}
                </Typography>
                <Typography color="text.secondary">
                  {group.tenantName || `Tenant ${group.tenantId ?? '-'}`}
                </Typography>
              </Stack>
              <Chip
                label={`${group.rows.length} assignment-uri`}
                color="primary"
                variant="outlined"
              />
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Assignment role</TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Overrides</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography fontWeight={700}>
                          {row.userName || row.userEmail || `User #${row.userId ?? '-'}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.userEmail || '-'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.assignmentRoleCode || '-'}</TableCell>
                    <TableCell>{row.permissionProfileCode || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {row.primary ? (
                          <Chip label="Primary" size="small" color="primary" />
                        ) : null}
                        <Chip
                          label={row.status || 'UNKNOWN'}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(row.allowOverrides || []).map((token) => (
                          <Chip
                            key={`allow-${row.id}-${token}`}
                            label={`ALLOW ${token}`}
                            size="small"
                          />
                        ))}
                        {(row.denyOverrides || []).map((token) => (
                          <Chip
                            key={`deny-${row.id}-${token}`}
                            label={`DENY ${token}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ))}
                        {!row.allowOverrides?.length && !row.denyOverrides?.length ? (
                          <Typography variant="body2" color="text.secondary">
                            none
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        </Paper>
      ))}

      {!groups.length && !loading ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Nu exista assignment-uri pentru filtrele curente.
          </Typography>
        </Paper>
      ) : null}
    </Stack>
  );
};

export default LocationTeamPage;
